import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Badge, Box, Tab } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import LabBrowser from 'components/lab-browser/lab-browser.component';
import ClinicRequests from 'components/clinic-requests/clinic-requests.component';
import { incomingClinicRequestsTemplate } from 'apollo/templates/clinic-request';
import { ClinicRequestStatus } from 'models/clinic-request';
import SimpleConnectedLabs from './connected-labs.component';

const MY_LABS = 'my-labs';
const FIND_LABS = 'find-labs';
const REQUESTS = 'requests';

/**
 * DD-3696: the Pro dentist's Labs page. "My labs" (the default) is the connected labs,
 * where a lab is disconnected as before; "Find labs" is the lab browser (built on Find
 * suppliers) and replaces the old connect list. The page opens on My labs; the old connect
 * route redirects here.
 * DD-3702: "Requests" — labs asking this clinic, and the clinic's own requests to labs.
 * A request notification opens it directly (?tab=requests).
 */
const SimpleLabs: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'client-directory']);
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('tab') === REQUESTS ? REQUESTS : MY_LABS);
    // Requests waiting for this clinic's answer, on the tab.
    const { data: pending } = useQuery(incomingClinicRequestsTemplate, {
        variables: { status: ClinicRequestStatus.PENDING },
        fetchPolicy: 'cache-and-network',
    });
    const pendingCount: number = pending?.incomingClinicRequests?.length ?? 0;

    return (
        <TabContext value={tab}>
            <PageTitle headingStyle="h1" title={translate('navigation:LABS')} />
            <Box mb={2}>
                <TabList onChange={(_, value: string) => setTab(value)} indicatorColor="primary" textColor="primary">
                    <Tab label={translate('client-directory:MY_LABS')} value={MY_LABS} data-test="labs.my-tab" />
                    <Tab label={translate('client-directory:FIND_LABS')} value={FIND_LABS} data-test="labs.find-tab" />
                    <Tab
                        value={REQUESTS}
                        data-test="labs.requests-tab"
                        label={
                            <Badge color="primary" badgeContent={pendingCount} sx={{ pr: pendingCount ? 1.5 : 0 }}>
                                {translate('client-directory:REQUESTS')}
                            </Badge>
                        }
                    />
                </TabList>
            </Box>
            <TabPanel sx={{ padding: 0 }} value={MY_LABS}>
                <SimpleConnectedLabs />
            </TabPanel>
            <TabPanel sx={{ padding: 0 }} value={FIND_LABS}>
                <LabBrowser />
            </TabPanel>
            <TabPanel sx={{ padding: 0 }} value={REQUESTS}>
                <ClinicRequests />
            </TabPanel>
        </TabContext>
    );
};

export default SimpleLabs;
